import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../../stores/auth.store';
import { api } from '../../lib/api';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { ContractStatusBanner } from '../../components/banking/ContractStatusBanner';
import { KycStatusCard } from '../../components/banking/KycStatusCard';
import {
  Wallet,
  Send,
  QrCode,
  ArrowUpRight,
  ArrowDownLeft,
  Clock,
  Bell,
  Eye,
  EyeOff,
  PieChart,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PieChart as RechartsPieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

// Cores para o gráfico de categorias
const CATEGORY_COLORS = ['#06b6d4', '#8b5cf6', '#f59e0b', '#ef4444', '#22c55e', '#ec4899'];

const CATEGORY_LABELS: Record<string, string> = {
  PIX_SEND: 'Pix Enviado',
  TRANSFER: 'Transferências',
  PAYMENT: 'Pagamentos',
  WITHDRAWAL: 'Saques',
  OTHER: 'Outros',
};

export function CustomerDashboard() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [showBalance, setShowBalance] = useState(true);
  const [analyticsPeriod, setAnalyticsPeriod] = useState<'7' | '30' | '90'>('30');

  // Fetch balance
  const { data: balanceData, isLoading: balanceLoading, error: balanceError } = useQuery({
    queryKey: ['banking', 'balance'],
    queryFn: async () => {
      const response = await api.get('/banking/balance');
      return response.data;
    },
    retry: false, // Don't retry on 404 (no account)
  });

  // Fetch recent transactions
  const { data: transactionsData, isLoading: transactionsLoading } = useQuery({
    queryKey: ['banking', 'transactions', 'recent'],
    queryFn: async () => {
      const response = await api.get('/banking/statement', { params: { take: 5 } });
      return response.data;
    },
    retry: false, // Don't retry on 404 (no account)
    enabled: !balanceError, // Only fetch if balance succeeded (user has account)
  });

  // Fetch notifications
  const { data: notificationsData } = useQuery({
    queryKey: ['banking', 'notifications'],
    queryFn: async () => {
      const response = await api.get('/banking/notifications', { params: { limit: 3 } });
      return response.data;
    },
    retry: false,
  });

  // Fetch spending analytics
  const { data: analyticsData, isLoading: analyticsLoading } = useQuery({
    queryKey: ['banking', 'analytics', 'spending', analyticsPeriod],
    queryFn: async () => {
      try {
        const response = await api.get('/banking/analytics/spending-by-category', {
          params: { days: analyticsPeriod },
        });
        return response.data;
      } catch {
        // Fallback: calcular a partir das transações
        return null;
      }
    },
    retry: false,
    enabled: !balanceError, // Only fetch if balance succeeded (user has account)
  });

  // Fetch contract status
  const { data: contractData } = useQuery({
    queryKey: ['banking', 'contract-status'],
    queryFn: async () => {
      try {
        const response = await api.get('/banking/contract-status');
        return response.data;
      } catch {
        // Se não houver endpoint, retornar status padrão (conta ativa)
        return {
          contractStatus: 'ACTIVE',
          kycOriginStatus: 'APPROVED',
          kycDestinationStatus: 'APPROVED',
        };
      }
    },
  });

  const formatCurrency = (value: number | string) => {
    const num = typeof value === 'string' ? parseInt(value, 10) || 0 : value;
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(num / 100);
  };

  const formatDate = (date: string) => {
    if (!date) return '';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(d);
  };

  // Se houver erro de conta não encontrada, mostrar mensagem apropriada
  if (balanceError) {
    return (
      <div className="space-y-6">
        {/* Welcome Section */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">
              Olá, {user?.name?.split(' ')[0]}!
            </h1>
            <p className="text-surface-400">Bem-vindo ao seu Internet Banking</p>
          </div>
        </div>

        {/* No Account Card */}
        <Card className="border-yellow-500/30">
          <div className="p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-yellow-500/20 flex items-center justify-center">
              <Wallet size={32} className="text-yellow-400" />
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">
              Conta não configurada
            </h2>
            <p className="text-surface-400 mb-6 max-w-md mx-auto">
              Você ainda não possui uma conta virtual vinculada ao seu perfil. 
              Entre em contato com o administrador para configurar sua conta.
            </p>
            <div className="flex justify-center gap-3">
              <Button
                variant="outline"
                onClick={() => navigate('/banking/settings')}
              >
                Configurações
              </Button>
              <Button
                variant="primary"
                onClick={() => navigate('/banking/kyc')}
              >
                Verificar Identidade
              </Button>
            </div>
          </div>
        </Card>

        {/* KYC Status Card - still show this */}
        <KycStatusCard />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Contract Status Banner */}
      {contractData && (
        <ContractStatusBanner
          contractStatus={contractData.contractStatus}
          kycOriginStatus={contractData.kycOriginStatus}
          kycDestinationStatus={contractData.kycDestinationStatus}
        />
      )}

      {/* KYC Pending Banner */}
      {contractData && contractData.kycOriginStatus === 'PENDING' && (
        <div className="glass rounded-xl p-4 border border-yellow-500/30 bg-yellow-500/5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                <Bell size={20} className="text-yellow-400" />
              </div>
              <div>
                <h3 className="font-medium text-yellow-400">Verificação KYC Pendente</h3>
                <p className="text-sm text-surface-400">
                  Complete sua verificação de identidade para liberar depósitos e operações
                </p>
              </div>
            </div>
            <Button
              variant="primary"
              size="sm"
              onClick={() => navigate('/banking/kyc')}
            >
              Verificar Agora
            </Button>
          </div>
        </div>
      )}

      {/* KYC Status Card */}
      <KycStatusCard />

      {/* Welcome Section */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">
            Olá, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-surface-400">Bem-vindo ao seu Internet Banking</p>
        </div>
      </div>

      {/* Balance Card */}
      <Card className="relative overflow-hidden border-primary-500/30">
        {/* Background gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary-600/20 via-primary-700/10 to-transparent pointer-events-none" />
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none" />
        
        <div className="relative p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-primary-500/20">
                <Wallet size={20} className="text-primary-400" />
              </div>
              <span className="text-surface-300 font-medium">Saldo disponível</span>
            </div>
            <button
              onClick={() => setShowBalance(!showBalance)}
              className="p-2 text-surface-400 hover:text-white rounded-lg hover:bg-surface-700/50 transition-colors"
            >
              {showBalance ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
          
          <div className="mb-6">
            {balanceLoading ? (
              <div className="h-10 w-48 bg-surface-700 rounded animate-pulse" />
            ) : (
              <span className="text-4xl font-bold text-white">
                {showBalance ? formatCurrency(balanceData?.availableBalance || 0) : '••••••'}
              </span>
            )}
            {!balanceLoading && balanceData?.blockedBalance > 0 && showBalance && (
              <p className="text-sm text-surface-400 mt-1">
                Bloqueado: {formatCurrency(balanceData.blockedBalance)}
              </p>
            )}
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-4 gap-3">
            <button
              onClick={() => navigate('/banking/customer/pix')}
              className="flex flex-col items-center gap-2 p-3 rounded-xl bg-surface-800/80 hover:bg-surface-700 border border-surface-700 hover:border-primary-500/50 transition-all"
            >
              <QrCode size={24} className="text-primary-400" />
              <span className="text-xs text-surface-300">Pix</span>
            </button>
            <button
              onClick={() => navigate('/banking/customer/transfers')}
              className="flex flex-col items-center gap-2 p-3 rounded-xl bg-surface-800/80 hover:bg-surface-700 border border-surface-700 hover:border-primary-500/50 transition-all"
            >
              <Send size={24} className="text-primary-400" />
              <span className="text-xs text-surface-300">Transferir</span>
            </button>
            <button
              onClick={() => navigate('/banking/customer/payments')}
              className="flex flex-col items-center gap-2 p-3 rounded-xl bg-surface-800/80 hover:bg-surface-700 border border-surface-700 hover:border-primary-500/50 transition-all"
            >
              <Wallet size={24} className="text-primary-400" />
              <span className="text-xs text-surface-300">Pagar</span>
            </button>
            <button
              onClick={() => navigate('/banking/customer/statement')}
              className="flex flex-col items-center gap-2 p-3 rounded-xl bg-surface-800/80 hover:bg-surface-700 border border-surface-700 hover:border-primary-500/50 transition-all"
            >
              <Clock size={24} className="text-primary-400" />
              <span className="text-xs text-surface-300">Extrato</span>
            </button>
          </div>
        </div>
      </Card>

      {/* Recent Transactions */}
      <Card>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">Últimas Transações</h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/banking/customer/statement')}
            >
              Ver todas
            </Button>
          </div>

          {transactionsLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-4 p-3 rounded-lg bg-surface-800/50 animate-pulse">
                  <div className="w-10 h-10 rounded-full bg-surface-700" />
                  <div className="flex-1">
                    <div className="h-4 w-32 bg-surface-700 rounded mb-2" />
                    <div className="h-3 w-24 bg-surface-700 rounded" />
                  </div>
                  <div className="h-5 w-20 bg-surface-700 rounded" />
                </div>
              ))}
            </div>
          ) : transactionsData?.entries?.length > 0 ? (
            <div className="space-y-2">
              {transactionsData.entries.slice(0, 5).map((tx: any) => {
                const isIncoming = tx.type === 'CREDIT';
                const counterparty = isIncoming
                  ? (tx.senderName || tx.metadata?.customerName || 'Recebido')
                  : (tx.metadata?.destinationKey || tx.description || tx.transactionType);
                const counterpartyLabel = isIncoming ? 'De' : 'Para';
                const displayName = (() => {
                  if (isIncoming) return counterparty;
                  // For outgoing, try to show a cleaner name
                  const destKey = tx.metadata?.destinationKey;
                  if (destKey) {
                    // Format CPF/CNPJ
                    const cleaned = destKey.replace(/\D/g, '');
                    if (cleaned.length === 11) return `CPF ***${cleaned.slice(6, 9)}-${cleaned.slice(9)}`;
                    if (cleaned.length === 14) return `CNPJ ${cleaned.slice(0, 2)}.***/${cleaned.slice(8, 12)}-${cleaned.slice(12)}`;
                    return destKey;
                  }
                  return tx.description || tx.transactionType || tx.type;
                })();
                return (
                <div
                  key={tx.id}
                  className="flex items-center gap-4 p-3 rounded-lg bg-surface-800/50 hover:bg-surface-800 transition-colors cursor-pointer group"
                  onClick={() => navigate('/banking/customer/statement')}
                >
                  <div className={`
                    w-10 h-10 rounded-full flex items-center justify-center
                    ${isIncoming
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-red-500/20 text-red-400'
                    }
                  `}>
                    {isIncoming
                      ? <ArrowDownLeft size={20} />
                      : <ArrowUpRight size={20} />
                    }
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">
                      {counterpartyLabel}: {displayName}
                    </p>
                    <p className="text-xs text-surface-400">
                      {tx.transactionType === 'PIX_OUT' ? 'PIX Enviado' : tx.transactionType === 'PIX_IN' ? 'PIX Recebido' : tx.transactionType || tx.type} • {formatDate(tx.createdAt || tx.date)}
                    </p>
                  </div>
                  <span className={`
                    text-sm font-semibold
                    ${isIncoming ? 'text-green-400' : 'text-red-400'}
                  `}>
                    {isIncoming ? '+' : '-'}
                    {formatCurrency(tx.amount)}
                  </span>
                </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <Clock size={48} className="mx-auto text-surface-600 mb-3" />
              <p className="text-surface-400">Nenhuma transação recente</p>
            </div>
          )}
        </div>
      </Card>

      {/* Notifications & Benefits Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Spending Analytics */}
        <Card>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <PieChart size={20} className="text-primary-400" />
                Gastos por Categoria
              </h2>
              <div className="flex gap-1">
                {(['7', '30', '90'] as const).map((period) => (
                  <button
                    key={period}
                    onClick={() => setAnalyticsPeriod(period)}
                    className={`px-2 py-1 text-xs rounded transition-colors ${
                      analyticsPeriod === period
                        ? 'bg-primary-500 text-white'
                        : 'bg-surface-700 text-surface-400 hover:bg-surface-600'
                    }`}
                  >
                    {period}d
                  </button>
                ))}
              </div>
            </div>

            {analyticsLoading ? (
              <div className="h-48 flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : analyticsData?.categories?.length > 0 ? (
              <div className="flex items-center gap-4">
                <div className="w-32 h-32">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPieChart>
                      <Pie
                        data={analyticsData.categories}
                        dataKey="total"
                        nameKey="category"
                        cx="50%"
                        cy="50%"
                        innerRadius={25}
                        outerRadius={50}
                        paddingAngle={2}
                      >
                        {analyticsData.categories.map((_: any, index: number) => (
                          <Cell key={`cell-${index}`} fill={CATEGORY_COLORS[index % CATEGORY_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value: number | undefined) => value !== undefined ? formatCurrency(value) : ''}
                        contentStyle={{
                          backgroundColor: '#1e293b',
                          border: '1px solid #334155',
                          borderRadius: '8px',
                        }}
                      />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex-1 space-y-2">
                  {analyticsData.categories.slice(0, 4).map((cat: any, index: number) => (
                    <div key={cat.category} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: CATEGORY_COLORS[index % CATEGORY_COLORS.length] }}
                        />
                        <span className="text-xs text-surface-300">
                          {CATEGORY_LABELS[cat.category] || cat.category}
                        </span>
                      </div>
                      <span className="text-xs font-medium text-white">
                        {formatCurrency(cat.total)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="h-48 flex flex-col items-center justify-center">
                <PieChart size={32} className="text-surface-600 mb-2" />
                <p className="text-sm text-surface-400">Sem dados de gastos</p>
                <p className="text-xs text-surface-500">no período selecionado</p>
              </div>
            )}
          </div>
        </Card>

        {/* Notifications */}
        <Card>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <Bell size={20} className="text-primary-400" />
                Notificações
              </h2>
            </div>

            {notificationsData?.notifications?.length > 0 ? (
              <div className="space-y-3">
                {notificationsData.notifications.slice(0, 3).map((notification: any) => (
                  <div
                    key={notification.id}
                    className={`
                      p-3 rounded-lg border-l-4
                      ${notification.read
                        ? 'bg-surface-800/30 border-surface-600'
                        : 'bg-primary-500/10 border-primary-500'
                      }
                    `}
                  >
                    <p className="text-sm text-white">{notification.title}</p>
                    <p className="text-xs text-surface-400 mt-1">{notification.message}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <Bell size={32} className="mx-auto text-surface-600 mb-2" />
                <p className="text-sm text-surface-400">Sem notificações</p>
              </div>
            )}
          </div>
        </Card>
      </div>

    </div>
  );
}

export default CustomerDashboard;
